import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, message, Modal, Row, Select, Space, Table, Tabs } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
    AvailableOrderType,
    GET_ORDER_LISTING_FOR_PICKUP,
    GetOrderListingForPickupResponse,
    GetOrderListingForPickupVariables
} from '../graphql/queries/getOrderListingForPickup';
import { UPDATE_APPOINTMENT_TRACKING_NUMBERS } from '../graphql/mutations/updateAppointmentTrackingNumbers';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { allChannelLiteResponse, GET_ALL_CHANNEL_LITE } from '../../../../graphql/queries/getChannelAll';
import { useTranslation } from 'react-i18next';
const { RangePicker } = DatePicker;

interface AddParcelsModalProps {
    visible: boolean;
    onClose: () => void;
    appointmentId: number;
    currentTrackingNumbers: string[];
    onSuccess: () => void;
    hubId: number;
    currentChannelId: number; // 新增：当前预约单的客户ID
}

const AddParcelsModal: React.FC<AddParcelsModalProps> = ({
    visible,
    onClose,
    appointmentId,
    currentTrackingNumbers,
    onSuccess,
    hubId,
    currentChannelId
}) => {
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [activeTab, setActiveTab] = useState('add');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [allSelectedRows, setAllSelectedRows] = useState<AvailableOrderType[]>([]);
    const [editedTrackingNumbers, setEditedTrackingNumbers] = useState<string>('');
    const [filters, setFilters] = useState({
        trackingNumbers: '',
        createdAtRange: null as [Dayjs, Dayjs] | null,
        bookDateRange: null as [Dayjs, Dayjs] | null,
    });
    // 已提交/生效的筛选条件，仅在点击 Filter 或 Reset 时更新，从而控制查询触发
    const [appliedFilters, setAppliedFilters] = useState({
        trackingNumbers: '',
        createdAtRange: null as [Dayjs, Dayjs] | null,
        bookDateRange: null as [Dayjs, Dayjs] | null,
    });
    const {t} = useTranslation();
    // 查询可用订单 - 固定使用当前预约单的客户ID
    const { data, loading } = useQuery<GetOrderListingForPickupResponse, GetOrderListingForPickupVariables>(
        GET_ORDER_LISTING_FOR_PICKUP,
        {
            variables: {
                filters: {
                    trackingNumbers: appliedFilters.trackingNumbers || undefined,
                    channelId: currentChannelId, // 固定使用当前预约单的客户ID
                    createdAt: appliedFilters.createdAtRange ? {
                        start: appliedFilters.createdAtRange[0].startOf('day').toISOString(),
                        end: appliedFilters.createdAtRange[1].endOf('day').toISOString(),
                    } : undefined,
                    expectedPickupDateRange: appliedFilters.bookDateRange ? {
                        start: appliedFilters.bookDateRange[0].format('YYYY-MM-DD'),
                        end: appliedFilters.bookDateRange[1].format('YYYY-MM-DD')
                    } : undefined,
                },
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                    sortBy: 'orderTime',
                    sortDirection: 'DESC'
                },
                hubId: hubId // 使用从外部传入的 Hub ID
            },
            skip: !visible,
            fetchPolicy: 'network-only'
        }
    );

    // 更新预约单追踪号的mutation
    const [updateAppointmentTrackingNumbers, { loading: updateLoading }] = useMutation(UPDATE_APPOINTMENT_TRACKING_NUMBERS);

    // 获取所有客户信息用于显示客户名称（轻量级，仅 id + channelName）
    const { data: channelData } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE);

    // 表格列定义
    const columns: ColumnsType<AvailableOrderType> = [
        {
            title: t('pickup.appointment.addParcelsModal.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            width: 200,
            render: (text: string) => (
                <span className="font-mono text-sm">{text}</span>
            ),
        },
        {
            title: t('pickup.appointment.addParcelsModal.client'),
            dataIndex: 'channelId',
            key: 'channelId',
            width: 120,
            render: (channelId: number, record) => {
                const channel = channelData?.getAllChannelsLite?.find(
                    (channel) => String(channel.id) === String(record.channelId)
                );
                return (
                    <span className="text-sm">{channel ? channel.channelName : (channelId || '--')}</span>
                );
            },
        },
        {
            title: t('pickup.appointment.addParcelsModal.createDate'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (createdAt: string) => (
                <span className="text-sm">
                    {createdAt ? dayjs(createdAt).format('YYYY-MM-DD HH:mm') : '--'}
                </span>
            ),
        },
        {
            title: t('pickup.appointment.addParcelsModal.pickupScheduledDate'),
            dataIndex: ['pickupInfo', 'expectedPickupDate'],
            key: 'expectedPickupDate',
            width: 180,
            render: (_, record: AvailableOrderType) => (
                <span className="text-sm">
                    {record.pickupInfo?.expectedPickupDate ?
                        dayjs(record.pickupInfo.expectedPickupDate).format('YYYY-MM-DD') : '--'}
                </span>
            ),
        },
        {
            title: t('pickup.appointment.addParcelsModal.status'),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            width: 120,
            render: (status: string) => (
                <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded">
                    {status}
                </span>
            ),
        },
    ];

    // 处理搜索
    const handleSearch = () => {
        setCurrentPage(1);
        setSelectedRowKeys([]);
        // 提交筛选条件，触发查询
        setAppliedFilters(filters);
    };

    // 处理重置
    const handleReset = () => {
        const defaults = {
            trackingNumbers: '',
            createdAtRange: null as [Dayjs, Dayjs] | null,
            bookDateRange: null as [Dayjs, Dayjs] | null,
        };
        setFilters(defaults);
        setAppliedFilters(defaults); // 重置后立即触发查询
        setCurrentPage(1);
        setSelectedRowKeys([]);
        form.resetFields();
    };

    // 处理分页变化
    const handleTableChange = (pagination: any) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
        // 清空当前页的选择，但保留所有页面的选择
        setSelectedRowKeys([]);
    };

    // 处理行选择变化
    const handleSelectionChange = (_: React.Key[], newSelectedRows: AvailableOrderType[]) => {
        // 过滤掉已经在当前预约单中的追踪号
        const filteredRows = newSelectedRows.filter(row =>
            !currentTrackingNumbers.includes(row.trackingNumber)
        );
        const filteredKeys = filteredRows.map(row => row.id);

        setSelectedRowKeys(filteredKeys);

        // 更新所有页面的选中行
        const currentPageRows = filteredRows;
        const otherPagesRows = allSelectedRows.filter(row =>
            !data?.getOrderListingForPickup.content.some(contentRow => contentRow.id === row.id)
        );
        const updatedAllSelectedRows = [...otherPagesRows, ...currentPageRows];
        setAllSelectedRows(updatedAllSelectedRows);
    };

    // 处理添加包裹
    const handleAddParcels = async () => {
        const totalSelectedCount = allSelectedRows.length;
        if (totalSelectedCount === 0) {
            message.warning('Please select parcels to add');
            return;
        }

        try {
            // 计算新的追踪号列表
            const newTrackingNumbers = allSelectedRows.map(row => row.trackingNumber);
            const updatedTrackingNumbers = [...currentTrackingNumbers, ...newTrackingNumbers];

            // 调用更新接口
            await updateAppointmentTrackingNumbers({
                variables: {
                    appointmentId: appointmentId,
                    trackingNumbers: updatedTrackingNumbers
                }
            });

            message.success(`Successfully added ${totalSelectedCount} parcel(s)`);

            // 清空选择状态
            setSelectedRowKeys([]);
            setAllSelectedRows([]);

            // 关闭弹框并刷新父组件
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Add parcels error:', error);

            // 提取具体的错误信息
            let errorMessage = 'Failed to add parcels';

            if (error?.graphQLErrors?.[0]?.message) {
                errorMessage = error.graphQLErrors[0].message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        }
    };

    // 初始化编辑表单数据
    useEffect(() => {
        if (visible) {
            const trackingNumbersText = currentTrackingNumbers.join('\n');
            setEditedTrackingNumbers(trackingNumbersText);
            editForm.setFieldsValue({
                trackingNumbers: trackingNumbersText
            });
        }
    }, [visible, editForm]); // 移除 currentTrackingNumbers 依赖

    // 重置选择状态当弹框关闭时
    useEffect(() => {
        if (!visible) {
            setSelectedRowKeys([]);
            setAllSelectedRows([]);
            setFilters({
                trackingNumbers: '',
                createdAtRange: null,
                bookDateRange: null,
            });
            setCurrentPage(1);
            setActiveTab('add');
            form.resetFields();
            editForm.resetFields();
        }
    }, [visible, form, editForm]);

    // 处理tab切换
    const handleTabChange = (key: string) => {
        if (key === 'edit') {
            // 切换到编辑tab时，重置编辑内容为原始数据
            const trackingNumbersText = currentTrackingNumbers.join('\n');
            setEditedTrackingNumbers(trackingNumbersText);
            editForm.setFieldsValue({
                trackingNumbers: trackingNumbersText
            });
        } else if (key === 'add') {
            // 切换到添加tab时，清空选择状态
            setSelectedRowKeys([]);
            setAllSelectedRows([]);
        }
        setActiveTab(key);
    };

    // 处理编辑保存
    const handleSaveEdit = async () => {
        try {
            const values = await editForm.validateFields();
            const trackingNumbersText = values.trackingNumbers || '';

            // 解析追踪号列表 - 支持多种分隔符（与后端标准保持一致）
            const newTrackingNumbers = trackingNumbersText
                .split(/[;,\n\r]+/)           // 按分号、逗号、换行符分割
                .map((line: string) => line.trim())  // 去除每个值的前后空白字符
                .filter((line: string) => line.length > 0);  // 过滤空字符串

            // 调用更新接口
            await updateAppointmentTrackingNumbers({
                variables: {
                    appointmentId: appointmentId,
                    trackingNumbers: newTrackingNumbers
                }
            });

            message.success('Tracking numbers updated successfully');

            // 关闭弹框并刷新父组件
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Update tracking numbers error:', error);

            // 提取具体的错误信息
            let errorMessage = 'Failed to update tracking numbers';

            if (error?.graphQLErrors?.[0]?.message) {
                errorMessage = error.graphQLErrors[0].message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        }
    };

    // 行选择配置
    const rowSelection = {
        selectedRowKeys,
        onChange: handleSelectionChange,
        getCheckboxProps: (record: AvailableOrderType) => ({
            disabled: currentTrackingNumbers.includes(record.trackingNumber), // 已存在的包裹禁用选择
        }),
    };

    // 渲染底部按钮
    const renderFooter = () => {
        if (activeTab === 'add') {
            return [
                <Button key="cancel" onClick={onClose} className="w-auto min-w-24">
                    {t('components.button.cancel')}
                </Button>,
                <Button
                    key="add"
                    type="primary"
                    loading={updateLoading}
                    disabled={allSelectedRows.length === 0}
                    onClick={handleAddParcels}
                    icon={<PlusOutlined />}
                    className="w-auto min-w-24"
                >
                    {t('pickup.appointment.addParcelsModal.addSelectedParcels', {count: allSelectedRows.length})}
                </Button>,
            ];
        } else {
            return [
                <Button key="cancel" onClick={onClose} className="w-auto min-w-24">
                    {t('components.button.cancel')}
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    loading={updateLoading}
                    onClick={handleSaveEdit}
                    icon={<EditOutlined />}
                    className="w-auto min-w-24"
                >
                    {t('pickup.appointment.addParcelsModal.saveChanges')}
                </Button>,
            ];
        }
    };

    return (
        <Modal
            title={t('pickup.appointment.addParcelsModal.manageParcels')}
            open={visible}
            onCancel={onClose}
            width={1200}
            footer={renderFooter()}
        >
            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={[
                    {
                        key: 'add',
                        label: t('pickup.appointment.addParcelsModal.addParcels'),
                        children: (
                            <>
                                {/* 筛选区域 */}
                                <div className="mb-4 p-4 bg-gray-50 rounded">
                                    <Form form={form} layout="vertical">
                                        <Row gutter={16}>
                                            <Col span={6}>
                                                <Form.Item label={t('pickup.appointment.addParcelsModal.trackingNumber')} name="trackingNumbers">
                                                    <Input
                                                        placeholder={t('pickup.appointment.addParcelsModal.enterTrackingNumber') || ''}
                                                        value={filters.trackingNumbers}
                                                        onChange={(e) => setFilters(prev => ({
                                                            ...prev,
                                                            trackingNumbers: e.target.value
                                                        }))}
                                                        allowClear
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item label={t('pickup.appointment.addParcelsModal.client')}>
                                                    <Input
                                                        value={channelData?.getAllChannelsLite?.find(
                                                            (channel) => String(channel.id) === String(currentChannelId)
                                                        )?.channelName || `Client ID: ${currentChannelId}`}
                                                        disabled
                                                        style={{ backgroundColor: '#f5f5f5' }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item label={t('pickup.appointment.addParcelsModal.createDate')} name="createdAtRange">
                                                    <RangePicker
                                                        style={{ width: '100%' }}
                                                        value={filters.createdAtRange}
                                                        onChange={(dates) => setFilters(prev => ({
                                                            ...prev,
                                                            createdAtRange: dates as [Dayjs, Dayjs] | null
                                                        }))}
                                                        placeholder={['Start Date', 'End Date']}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item label={t('pickup.appointment.addParcelsModal.pickupScheduledDate')} name="bookDateRange">
                                                    <RangePicker
                                                        style={{ width: '100%' }}
                                                        value={filters.bookDateRange}
                                                        onChange={(dates) => setFilters(prev => ({
                                                            ...prev,
                                                            bookDateRange: dates as [Dayjs, Dayjs] | null
                                                        }))}
                                                        placeholder={['Start Date', 'End Date']}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row justify="end">
                                            <Col>
                                                <Space>
                                                    <Button
                                                        type="primary"
                                                        onClick={handleSearch}
                                                        loading={loading}
                                                    >
                                                        {t('components.button.filter')}
                                                    </Button>
                                                    <Button
                                                        onClick={handleReset}
                                                    >
                                                        {t('components.button.reset')}
                                                    </Button>
                                                </Space>
                                            </Col>
                                        </Row>
                                    </Form>
                                </div>

                                {/* 表格 */}
                                <Table
                                    rowSelection={rowSelection}
                                    columns={columns}
                                    dataSource={data?.getOrderListingForPickup.content || []}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{
                                        current: currentPage,
                                        pageSize: pageSize,
                                        total: data?.getOrderListingForPickup.totalElements || 0,
                                        showSizeChanger: true,
                                        showQuickJumper: true,
                                        showTotal: (total) => t('table.pagination.totalItems', { total }),
                                        pageSizeOptions: ['10', '20', '50'],
                                    }}
                                    onChange={handleTableChange}
                                    size="small"
                                    scroll={{ x: 900 }}
                                />
                            </>
                        ),
                    },
                    {
                        key: 'edit',
                        label: t('pickup.appointment.addParcelsModal.editTrackingNumbers'),
                        children: (
                            <div className="p-4">
                                <Form form={editForm} layout="vertical">
                                    <Form.Item
                                        label={t('pickup.appointment.addParcelsModal.trackingNumbers')}
                                        name="trackingNumbers"
                                    >
                                        <Input.TextArea
                                            rows={15}
                                            placeholder={t('pickup.appointment.addParcelsModal.enterTrackingNumbers') || ''}
                                            value={editedTrackingNumbers}
                                            onChange={(e) => setEditedTrackingNumbers(e.target.value)}
                                        />
                                    </Form.Item>
                                    <div className="text-sm text-gray-500">
                                        {t('pickup.appointment.addParcelsModal.currentCount')}: {editedTrackingNumbers.split('\n').filter(line => line.trim().length > 0).length} {t('pickup.appointment.addParcelsModal.trackingNumbers')}
                                    </div>
                                </Form>
                            </div>
                        ),
                    },
                ]}
            />
        </Modal>
    );
};

export default AddParcelsModal; 